import { Button, Flex } from "@amzn/ring-ui-react-components";
import { FormCheckbox, FormInput, FormLabel, FormTextarea } from ".";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { getFormValidations } from "./FormValidations";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useEffect, useRef, useState } from "react";
import CaptchaDiv from "../CaptchaModal/CaptchaModal";
import Select from "react-select";
import { Document } from "@contentful/rich-text-types";
import { useMobileView } from "../../hooks";
import { SuccessMessage } from "./SuccessMessage";

export interface ProgramFormProps {
  formFields: any[];
  programId: string;
  leadSource: string;
  compositeLeadSource?: leadSourceType;
  organizationType: string;
  country: string;
  isFormExpired: boolean;
  successMessage: Document;
  submitButtonText?: string;
  inlineSuccessMessage?: string;
}

export interface leadSourceType {
  dependsOn: string;
  keyValueMapping: any;
}

export interface IFormInput {
  [key: string]: any;
}

export const ProgramForm = (props: ProgramFormProps) => {
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    getValues,
  } = useForm<IFormInput>();
  const [responseComponent, setResponseComponent] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const captchaRef = useRef();
  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    captchaRef.current.displayCaptchaModal();
    createPayload(getValues());
  };
  const mobileView = useMobileView();
  const markAsSubmitted = (component: any) => {
    setResponseComponent(component);
  };
  // On a successful submit, when the entry provides formConfig.inlineSuccessMessage
  // we show that text inline right above the Submit button (form stays in place, no
  // page swap, no redirect). Otherwise we keep the existing full success message, so
  // any entry without the message is unaffected.
  const onSubmitSuccess = () => {
    if (props.inlineSuccessMessage) {
      setSubmitted(true);
    } else {
      markAsSubmitted(<SuccessMessage successMessage={props.successMessage} />);
    }
  };

  const sendButton = {
    width: "30%",
    lineHeight: "28px",
    padding: "12px 20px 12px 20px",
    fontSize: "18px",
    fontWeight: 700,
    fontFamily: "RingSans, Helvetica, Arial, sans-serif",
    margin: "45px auto 30px auto",
    border: "0px",
    minWidth: "200px",
    borderRadius: "8px",
    color: "#fafbfc",
    background: "#121212",
  };
  // Grayed-out overrides applied once the form is submitted. The button is
  // already disabled={submitted}, but sendButton hardcodes an inline background
  // that beats the component's built-in :disabled styling, so without this it
  // still looks active. Only reached on inline-success forms (where the form
  // stays on screen); full-success forms swap the whole form out.
  const sendButtonDisabled = {
    background: "#8c8c8c",
    color: "#e0e0e0",
    cursor: "not-allowed",
  };
  const watermarkStyle = {
    position: "absolute",
    width: "700px",
    height: "100%",
    "background-color": "#f2f2f2b0",
    "z-index": "10000",
    "font-size": "5rem",
    "text-align": "center",
    color: "#5c5c5c",
    "font-weight": "100",
    "line-height": "95px",
    "flex-direction": "column",
    "justify-content": "center",
  };

  const createPayload = (formData: IFormInput) => {
    for (var key in formData) {
      if (typeof formData[key] == "boolean") {
        formData[key] = formData[key] == "true" ? 1 : 0;
      } else if (typeof formData[key] == "object") {
        if (Object.prototype.toString.call(formData[key]) == "[object Date]") {
          formData[key] =
            formData[key].getFullYear() +
            "-" +
            (formData[key].getMonth() + 1).toString().padStart(2, "0") +
            "-" +
            formData[key].getDate().toString().padStart(2, "0");
        } else {
          formData[key] = formData[key].value;
        }
      }
    }

    if (props.country != "N/A") {
      formData["Country"] = props.country;
    }

    if (props.organizationType != "N/A") {
      formData["Organization_Type__c"] = props.organizationType;
    }

    if (props.leadSource != "N/A") {
      formData["LeadSource"] = props.leadSource;
    } else {
      if (props.compositeLeadSource?.dependsOn) {
        const leadSource = formData[props.compositeLeadSource?.dependsOn];
        formData["LeadSource"] =
          props.compositeLeadSource?.keyValueMapping[leadSource];
        delete formData[props.compositeLeadSource?.dependsOn];
      }
    }

    return formData;
  };

  const createOptions = (options: any) => {
    if (options == undefined) return undefined; // TO-DO : Add error handling
    return Object.keys(options).map((key) => {
      return { value: options[key], label: key };
    });
  };

  return (
    <Flex style={{ position: "relative", justifyContent: "center" }}>
      {responseComponent ? (
        <div style={{ marginBottom: "220px", marginTop: "250px" }}>
          {responseComponent}
        </div>
      ) : (
        <>
          {props.isFormExpired && (
            <Flex style={watermarkStyle}>
              This program is no longer accepting applications.
            </Flex>
          )}
          <Flex
            marginLeft="auto"
            marginRight="auto"
            maxWidth="700px"
            flexDirection="column"
            as="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            {props.formFields.map((row) => {
              return (
                <Flex
                  key={row.rowId}
                  value={row.rowId}
                  style={{ flexDirection: mobileView ? "column" : "row" }}
                >
                  {row.rowCollection.items.map((rowItem: any, index: any) => {
                    const formFieldType = rowItem.__typename;
                    switch (formFieldType) {
                      case "CppFormFieldlabel":
                        return (
                          <FormLabel
                            labelData={rowItem.title}
                            labelAlignment={rowItem.alignment}
                            labelSize={rowItem.size}
                          />
                        );
                      case "CppFormFieldinput":
                        if (rowItem.type == "TEXTAREA")
                          return (
                            <FormTextarea
                              placeholder={rowItem.placeholder}
                              register={register}
                              name={rowItem.htmlName}
                              errors={errors}
                              validations={getFormValidations(rowItem)}
                            />
                          );
                        else if (rowItem.type == "DATE")
                          return (
                            <div
                              style={{ width: "100%", paddingRight: "30px" }}
                            >
                              {rowItem.label && (
                                <FormLabel
                                  labelData={rowItem.label}
                                  labelAlignment={"L"}
                                  labelSize={"M"}
                                />
                              )}
                              <Controller
                                control={control}
                                name={rowItem.htmlName}
                                rules={{ required: rowItem.isRequired }}
                                render={({ field: { ...fieldProps } }) => {
                                  return (
                                    <ReactDatePicker
                                      {...fieldProps}
                                      className={`custom-react-datepicker-input ${rowItem.htmlName}`}
                                      wrapperClassName="custom-react-datepicker-wrapper"
                                      placeholderText={rowItem.placeholder}
                                      selected={
                                        fieldProps.value
                                          ? new Date(fieldProps.value)
                                          : null
                                      }
                                      minDate={
                                        rowItem.dateValidations == "FUTURE_DATE"
                                          ? new Date()
                                          : null
                                      }
                                      maxDate={
                                        rowItem.dateValidations == "PAST_DATE"
                                          ? new Date()
                                          : null
                                      }
                                    />
                                  );
                                }}
                              />
                              {errors[rowItem.htmlName] && (
                                <span style={{ color: "#ff0000" }}>
                                  This Field is Required
                                </span>
                              )}
                            </div>
                          );
                        return (
                          <FormInput
                            placeholder={rowItem.placeholder}
                            register={register}
                            name={rowItem.htmlName}
                            errors={errors}
                            validations={getFormValidations(rowItem)}
                          />
                        );
                      case "CppFormFieldSelect": {
                        if (rowItem.type == "DROPDOWN") {
                          return (
                            <div
                              style={{
                                width: "100%",
                                paddingRight: "30px",
                                marginBottom: "15px",
                              }}
                            >
                              {rowItem.label && (
                                <FormLabel
                                  labelData={rowItem.label}
                                  labelAlignment={"L"}
                                  labelSize={"M"}
                                />
                              )}
                              <Controller
                                name={rowItem.htmlName}
                                control={control}
                                rules={{ required: rowItem.isRequired }}
                                render={({ field: { ...fieldProps } }) => (
                                  <Select
                                    {...fieldProps}
                                    options={createOptions(rowItem.options)}
                                    placeholder={rowItem.placeholder}
                                  />
                                )}
                              />
                              {errors[rowItem.htmlName] && (
                                <span style={{ color: "#ff0000" }}>
                                  This Field is Required
                                </span>
                              )}
                            </div>
                          );
                        } else if (rowItem.type == "CHECKBOX") {
                          return (
                            <FormCheckbox
                              name={rowItem.htmlName}
                              label={rowItem.placeholder}
                              register={register}
                              errors={errors}
                              validations={getFormValidations(rowItem)}
                            />
                          );
                        }
                      }
                      case "CppFormFieldempty":
                        return <></>;
                    }
                  })}
                </Flex>
              );
            })}
            {/* Wrapper is the positioning context + owns the button's vertical
                margins and centering. The confirmation is position:absolute, so
                it floats just above the button WITHOUT taking flow space — the
                button stays exactly where it is when the message appears. */}
            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                margin: "30px 0 30px",
              }}
            >
              {submitted && (
                <p
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    margin: "0 0 8px",
                    color: "#4B873D",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    lineHeight: "1.125rem",
                    fontFamily: "RingSans, Helvetica, Arial, sans-serif",
                    textAlign: "center",
                  }}
                >
                  {props.inlineSuccessMessage}
                </p>
              )}
              <Button
                type="submit"
                style={{
                  ...sendButton,
                  margin: 0,
                  ...(submitted ? sendButtonDisabled : {}),
                }}
                onClick={handleSubmit(onSubmit)}
                disabled={submitted}
              >
                {props.submitButtonText || "Send"}
              </Button>
            </div>
            <CaptchaDiv
              entryId={props.programId}
              entries={createPayload(getValues())}
              markAsSubmitted={markAsSubmitted}
              onSuccess={onSubmitSuccess}
              ref={captchaRef}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
};
